import {
  Building2,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  User as UserIcon,
  type LucideIcon,
} from 'lucide-react';
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router';
import { cn } from '../lib/cn';

/** A leaf entry inside an expandable group. */
export interface SidebarChildItem {
  id: string;
  label: string;
  href: string;
  /** When `true`, the entry is omitted from the rendered tree (RBAC). */
  hidden?: boolean;
}

/** Top-level menu entry — either a direct link or an expandable group. */
export interface SidebarItem {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Direct link target. Mutually exclusive with `children`. */
  href?: string;
  /** Sub-entries — when present, the item renders as an expandable group. */
  children?: SidebarChildItem[];
  /**
   * Path prefix(es) that mark this item as active. Defaults to `href` for
   * leaf items, or the union of children hrefs for groups.
   */
  matchPrefix?: string | string[];
  /** Treat the literal `/` route as if it matched this item. */
  matchRoot?: boolean;
  /**
   * Initial open state for groups wherever they expand inline — the mobile
   * drawer and the full-width desktop sidebar. Defaults to "open if currently
   * active". It has no effect on the collapsed rail, where a group opens as a
   * flyout on demand.
   */
  defaultOpen?: boolean;
  /** Hide this item entirely (e.g., RBAC). */
  hidden?: boolean;
  /** Optional counter pill rendered on the right (e.g., pending approvals). */
  badge?: string | number;
}

export interface SidebarUser {
  displayName: string;
  role?: string;
}

export interface SidebarBrand {
  title: string;
  /** Lucide icon to render in the brand square. Defaults to Building2. */
  icon?: LucideIcon;
}

export interface SidebarProps {
  /** Mobile drawer open state. Ignored on lg+ where the sidebar is static. */
  isOpen: boolean;
  onToggle: () => void;

  /** Brand block at the top-left. */
  brand: SidebarBrand;
  /** Optional slot under the brand title (e.g., TenantSwitcher). */
  headerSlot?: React.ReactNode;

  /** Top-level menu structure. */
  items: SidebarItem[];

  /** User block at the bottom. */
  user: SidebarUser;
  onSignOut: () => void;
}

/**
 * The icon column.
 *
 * <p>
 * Every icon in the sidebar — brand, nav, avatar, sign-out — is centred on the
 * same vertical line, 32px from the left edge, which is the middle of the 64px
 * rail. Collapsing therefore moves nothing sideways: the labels stop being
 * drawn and the panel narrows around an icon that has not budged. The padding
 * below is what puts each one there, given its own size and what it sits in;
 * nothing whose width changes with the state is centred with
 * `justify-center` — centring in a box that narrows is exactly what made the
 * icons jump (the fixed-size brand tile and avatar still centre their own
 * contents, which is a different thing).
 * </p>
 *
 * <p>
 * Row heights are STATED, never left to the content, so nothing drifts down the
 * list either. A 20px icon and a 24px line of label do not make the same row:
 * left to themselves the rail's rows come out 4px shorter than the expanded
 * ones, which is invisible at the top and half an icon out by the bottom. Each
 * height below is the one the expanded menu already had, so the rail is what
 * moves to meet it. The header keeps a floor of 64px for the same reason —
 * without it the whole nav rides up when the title stops being drawn.
 * </p>
 */
/**
 * A nav row: 32px tall, with a 20px icon at 22–42 (the `px-2` nav's 8, plus 14).
 */
const NAV_ROW = 'h-8 pl-3.5 pr-2';

/** Pointer that can hover, so the peek is not armed for a finger. */
const HOVER_QUERY = '(hover: hover)';

/**
 * How long the pointer must rest on the rail before it opens, and how long it
 * may stray before it shuts. Without the first, the menu flashes open every
 * time somebody crosses the rail on their way to the page; without the second,
 * it flickers on the gap between an icon and its label.
 */
const PEEK_OPEN_MS = 140;
const PEEK_CLOSE_MS = 220;

/**
 * Where the collapsed/expanded choice is remembered. Every app runs on its own
 * origin, so each keeps its own preference even though the key is shared.
 */
const COLLAPSED_STORAGE_KEY = 'my.sidebar.collapsed';

/** Tailwind's `lg` breakpoint — the point where the drawer becomes static. */
const DESKTOP_QUERY = '(min-width: 1024px)';

/**
 * A viewer convenience, not state that must survive: storage can be blocked
 * (private windows, cleared site data), and the sidebar simply opens expanded
 * when it is.
 */
function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    if (collapsed) window.localStorage.setItem(COLLAPSED_STORAGE_KEY, '1');
    else window.localStorage.removeItem(COLLAPSED_STORAGE_KEY);
  } catch {
    // Not persisting is fine — the toggle still works for this visit.
  }
}

/**
 * Whether the static desktop sidebar is showing, rather than the mobile
 * drawer. Collapsing and flyouts only make sense on desktop: the drawer is
 * already a full-width overlay, and a flyout to its right would open off the
 * edge of a phone screen.
 */
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
}

function asArray(v: string | string[] | undefined): string[] {
  if (!v) return [];
  return Array.isArray(v) ? v : [v];
}

function isItemActive(pathname: string, item: SidebarItem): boolean {
  const prefixes = asArray(item.matchPrefix);
  if (prefixes.length === 0) {
    if (item.href) prefixes.push(item.href);
    if (item.children) prefixes.push(...item.children.map((c) => c.href));
  }
  if (item.matchRoot && pathname === '/') return true;
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function isChildActive(pathname: string, child: SidebarChildItem): boolean {
  return pathname === child.href || pathname.startsWith(`${child.href}/`);
}

/** True on a device whose pointer hovers — a mouse, not a finger. */
function useCanHover(): boolean {
  const [canHover, setCanHover] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(HOVER_QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(HOVER_QUERY);
    const onChange = (e: MediaQueryListEvent) => setCanHover(e.matches);
    mq.addEventListener('change', onChange);
    setCanHover(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return canHover;
}

/**
 * Shared shell sidebar. Keeps the Old TMS look-and-feel (slate-800 chrome,
 * blue-600 active state, lucide icons) but is fully data-driven so each app
 * (Tms / Depot / Expenses) supplies its own menu, brand, and user info.
 *
 * Notes vs. My.Tms.Old:
 *   - No Auth0 / MobX / tenant coupling — caller passes `user` + `onSignOut`
 *     and slots in a `headerSlot` (TenantSwitcher, etc.).
 *   - `react-router` (v7) instead of `react-router-dom`.
 *   - RBAC is handled by the caller via the `hidden` flag on items/children.
 *
 * <p>
 * On desktop the sidebar collapses to an icon rail, remembered per browser in
 * localStorage. A group's sub-items follow the width they have to live in: at
 * full width they expand INLINE, under the parent, where there is room for them
 * and the shape of the menu stays visible; on the rail there is no room, so they
 * open as a flyout beside it. The mobile drawer is full width, so it expands
 * inline too.
 * </p>
 *
 * <p>
 * Resting the pointer on the rail PEEKS: the full menu slides out over the page
 * and shuts again when the pointer leaves or a destination is picked. The rail's
 * own 64px is held by a spacer while it does, so the peek floats above the page
 * instead of shoving it sideways — a menu that reflowed the whole screen every
 * time somebody's mouse crossed it would be unusable. The collapse PREFERENCE is
 * untouched by all this: peeking is a look, not a change of mind, which is why
 * it shuts itself. It is armed only on a pointer that hovers, so a touch screen
 * still gets the flyout.
 * </p>
 */
export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onToggle,
  brand,
  headerSlot,
  items,
  user,
  onSignOut,
}) => {
  const location = useLocation();
  const BrandIcon = brand.icon ?? Building2;
  const isDesktop = useIsDesktop();
  const [collapsedPreference, setCollapsedPreference] = useState<boolean>(readCollapsed);
  // The preference is kept while on a phone but not applied — the drawer
  // always opens full width, and the rail comes back on the next wide screen.
  const collapsed = isDesktop && collapsedPreference;

  // One flyout at a time, so opening a second group closes the first.
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  const closeFlyout = useCallback(() => setOpenGroupId(null), []);

  // ── Hover peek ────────────────────────────────────────────────────────
  const canHover = useCanHover();
  const [peeked, setPeeked] = useState(false);
  const peekTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Only the rail peeks: at full width there is nothing to reveal.
  const peeking = collapsed && peeked;
  // What the sidebar RENDERS as. `collapsed` stays the stored preference.
  const railed = collapsed && !peeked;

  const clearPeekTimer = () => {
    if (peekTimer.current) clearTimeout(peekTimer.current);
    peekTimer.current = null;
  };
  const schedulePeek = (next: boolean) => {
    clearPeekTimer();
    peekTimer.current = setTimeout(() => setPeeked(next), next ? PEEK_OPEN_MS : PEEK_CLOSE_MS);
  };
  const closePeekNow = useCallback(() => {
    if (peekTimer.current) clearTimeout(peekTimer.current);
    peekTimer.current = null;
    setPeeked(false);
  }, []);

  // Timers outlive the render that set them.
  useEffect(() => clearPeekTimer, []);
  // A preference change makes any peek meaningless.
  useEffect(() => {
    if (!collapsed || !canHover) closePeekNow();
  }, [collapsed, canHover, closePeekNow]);

  // Escape shuts the peek, as it does every other transient panel.
  useEffect(() => {
    if (!peeking) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePeekNow();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [peeking, closePeekNow]);

  // Navigating anywhere dismisses both — the flyout, and the peek that was
  // only ever open to let somebody choose.
  useEffect(() => {
    setOpenGroupId(null);
    closePeekNow();
  }, [location.pathname, closePeekNow]);

  const toggleCollapsed = () => {
    const next = !collapsedPreference;
    setCollapsedPreference(next);
    writeCollapsed(next);
    setOpenGroupId(null);
    closePeekNow();
  };

  return (
    <>
      {/* The rail's 64px, held while the peek floats out of the flow. Without
          it the page would jump left as the menu opens and back as it shuts. */}
      {peeking && <div className="hidden w-16 shrink-0 lg:block" aria-hidden="true" />}
      <div
        data-sidebar
        // Pointer: rest to open, leave to shut — both on a delay, so crossing
        // the rail does not flash the menu open.
        onMouseEnter={collapsed && canHover ? () => schedulePeek(true) : undefined}
        onMouseLeave={collapsed && canHover ? () => schedulePeek(false) : undefined}
        // Keyboard: tabbing in opens it at once (waiting would be nonsense for
        // a key press), and it shuts when focus leaves the sidebar entirely.
        onFocusCapture={
          collapsed
            ? () => {
                clearPeekTimer();
                setPeeked(true);
              }
            : undefined
        }
        onBlurCapture={
          collapsed
            ? (e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  closePeekNow();
                }
              }
            : undefined
        }
        // Picking a destination is the end of the peek. Checked on the anchor
        // so expanding a group — which is not a destination — leaves it open.
        onClick={
          peeking
            ? (e) => {
                if ((e.target as HTMLElement).closest('a')) closePeekNow();
              }
            : undefined
        }
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-slate-800 text-white transition-[transform,width] duration-300 ease-in-out',
          railed ? 'w-16' : 'w-64',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0',
          // A peek sits ON the page; every other state sits beside it.
          peeking ? 'lg:fixed lg:shadow-2xl lg:shadow-slate-900/30' : 'lg:static lg:inset-0',
        )}
      >
        {/* Header */}
        <div
          // px-4 puts the 32px brand tile at 16–48: centred on the same
          // line as every icon below it. min-h-16 keeps the nav starting at
          // the same y once the title and account switcher stop being drawn.
          className={cn(
            'flex min-h-16 items-center justify-between border-b border-slate-700 px-4 py-1.5',
          )}
        >
          <div className="flex min-w-0 items-center">
            {/* On the rail the app icon IS the expand control — there is no
              room for a separate one, and it's the obvious thing to click. */}
            {railed ? (
              <button
                type="button"
                onClick={toggleCollapsed}
                aria-label="Expand sidebar"
                title="Expand sidebar"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-600 transition-colors hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
              >
                <BrandIcon className="h-5 w-5" />
              </button>
            ) : (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-600">
                <BrandIcon className="h-5 w-5" />
              </div>
            )}
            {/* The header slot (account switcher) goes with the title: it has
              no icon-only form, and expanding the rail brings it back. */}
            {!railed && (
              <div className="ml-3 min-w-0 flex-1">
                <h1 className="text-lg font-semibold">{brand.title}</h1>
                {headerSlot}
              </div>
            )}
          </div>
          {/* The collapse control, and its opposite.
              Top-aligned so it sits level with the title rather than centred
              against the title + account switcher.

              While PEEKING it points the other way and keeps the sidebar open:
              a peek shuts itself, so without this the only way back from the
              rail is the brand tile, which does not look like a control. It
              only shows at full width — on the rail there is no room beside
              the 32px tile, and hovering brings the peek (and this) out. */}
          {isDesktop && !railed && (
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-label={collapsed ? 'Keep sidebar expanded' : 'Collapse sidebar'}
              title={collapsed ? 'Keep sidebar expanded' : 'Collapse sidebar'}
              className="ml-2 shrink-0 self-start rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-700 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
            >
              {collapsed ? (
                <ChevronsRight className="h-4 w-4" />
              ) : (
                <ChevronsLeft className="h-4 w-4" />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={onToggle}
            className="rounded-md p-2 hover:bg-slate-700 lg:hidden"
            aria-label="Close sidebar"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-2 px-2 py-2">
          {items
            .filter((item) => !item.hidden)
            .map((item) =>
              item.children ? (
                // Only the rail has nowhere to put the children. A peek is full
                // width, so its groups expand inline like any other full width.
                railed ? (
                  <SidebarFlyoutGroup
                    key={item.id}
                    item={item}
                    pathname={location.pathname}
                    open={openGroupId === item.id}
                    onToggle={() => setOpenGroupId((cur) => (cur === item.id ? null : item.id))}
                    onClose={closeFlyout}
                  />
                ) : (
                  <SidebarGroup key={item.id} item={item} pathname={location.pathname} />
                )
              ) : (
                <SidebarLeaf
                  key={item.id}
                  item={item}
                  pathname={location.pathname}
                  collapsed={railed}
                />
              ),
            )}
        </nav>

        {/* User section */}
        {/* px-3 puts the 40px avatar at 12-52 — the same centre line as
            every icon above it — and the padding no longer changes with the
            state, so the footer does not shuffle as the panel opens. */}
        <div className="mt-auto border-t border-slate-700 px-3 py-4">
          {/* h-11 is what the two lines of name + role already came to; the
              rail keeps it so the avatar and Sign Out do not ride up. */}
          <div
            className="mb-4 flex h-11 items-center"
            title={railed ? [user.displayName, user.role].filter(Boolean).join(' · ') : undefined}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-600">
              <UserIcon className="h-5 w-5" />
            </div>
            {!railed && (
              <div className="ml-3">
                <p className="font-medium">{user.displayName}</p>
                {user.role && <p className="text-sm text-slate-400">{user.role}</p>}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onSignOut}
            aria-label={railed ? 'Sign Out' : undefined}
            title={railed ? 'Sign Out' : undefined}
            // pl-2.5 inside the footer's px-3: 12 + 10 = 22, the icon line.
            className={
              'flex h-10 w-full items-center rounded-lg pl-2.5 pr-2 text-slate-300 transition-colors hover:bg-slate-700 hover:text-white'
            }
          >
            <LogOut className="h-5 w-5 shrink-0" />
            {!railed && <span className="ml-3">Sign Out</span>}
          </button>
        </div>
      </div>
    </>
  );
};

interface SidebarLeafProps {
  item: SidebarItem;
  pathname: string;
  collapsed: boolean;
}

const SidebarLeaf: React.FC<SidebarLeafProps> = ({ item, pathname, collapsed }) => {
  if (!item.href) return null;
  const Icon = item.icon;
  const active = isItemActive(pathname, item);
  return (
    <Link
      to={item.href}
      aria-label={collapsed ? item.label : undefined}
      title={collapsed ? item.label : undefined}
      className={cn(
        'relative flex items-center rounded-lg transition-colors',
        NAV_ROW,
        active ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white',
      )}
    >
      <Icon className="h-5 w-5 shrink-0" />
      {!collapsed && <span className="ml-3 font-medium">{item.label}</span>}
      {item.badge !== undefined &&
        (collapsed ? (
          // No room beside the icon on the rail — the count sits on its corner.
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white">
            {item.badge}
          </span>
        ) : (
          <span className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 py-0.5 text-xs font-semibold text-white">
            {item.badge}
          </span>
        ))}
    </Link>
  );
};

interface SidebarGroupProps {
  item: SidebarItem;
  pathname: string;
}

/**
 * Inline-expanding group: the form used at every width that has room for it —
 * the mobile drawer and the full-width desktop sidebar. Only the collapsed rail
 * swaps it for a flyout.
 */
const SidebarGroup: React.FC<SidebarGroupProps> = ({ item, pathname }) => {
  const active = isItemActive(pathname, item);
  const Icon = item.icon;
  const [open, setOpen] = useState<boolean>(item.defaultOpen ?? active);
  const Chevron = open ? ChevronDown : ChevronRight;
  const visibleChildren = (item.children ?? []).filter((c) => !c.hidden);
  if (visibleChildren.length === 0) return null;

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center justify-between rounded-lg',
          NAV_ROW,
          active ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white',
        )}
      >
        <span className="flex items-center">
          <Icon className="h-5 w-5 shrink-0" />
          <span className="ml-3 font-medium">{item.label}</span>
        </span>
        <Chevron className="h-4 w-4" />
      </button>
      {open && (
        <div className="ml-8 space-y-1">
          {visibleChildren.map((child) => (
            <Link
              key={child.id}
              to={child.href}
              className={cn(
                'block rounded-md py-1 pl-3.5 pr-2 text-sm',
                isChildActive(pathname, child)
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white',
              )}
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

interface SidebarFlyoutGroupProps {
  item: SidebarItem;
  pathname: string;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

/** Gap between the sidebar's right edge and the flyout, in px. */
const FLYOUT_GAP = 8;
/** Closest the flyout may sit to the top or bottom of the viewport, in px. */
const FLYOUT_EDGE = 8;

/**
 * The collapsed rail's group: an icon button that opens a popup menu beside the
 * rail. Used only when collapsed — at full width the children expand inline
 * instead, which is what there is room for.
 *
 * <p>
 * Portalled to `document.body` and `fixed`-positioned from the button's rect,
 * so no ancestor's overflow or stacking context can clip it — the rail is only
 * 64px wide, and the flyout has to sit entirely outside it.
 * </p>
 */
const SidebarFlyoutGroup: React.FC<SidebarFlyoutGroupProps> = ({
  item,
  pathname,
  open,
  onToggle,
  onClose,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  // A keyboard-activated open moves focus into the menu; a mouse click
  // leaves it where the pointer is.
  const focusOnOpen = useRef(false);

  // Anchor beside the sidebar, level with the button.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      setPosition(null);
      return;
    }
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const sidebarRight =
      buttonRef.current.closest('[data-sidebar]')?.getBoundingClientRect().right ??
      buttonRect.right;
    setPosition({ top: buttonRect.top, left: sidebarRight + FLYOUT_GAP });
  }, [open]);

  // Once the panel has a height, pull it up if it would run off the bottom.
  // Only sets when the value moves, so it settles instead of looping.
  useLayoutEffect(() => {
    if (!open || !position || !panelRef.current) return;
    const maxTop = window.innerHeight - panelRef.current.offsetHeight - FLYOUT_EDGE;
    const clamped = Math.max(FLYOUT_EDGE, Math.min(position.top, maxTop));
    if (clamped !== position.top) setPosition({ ...position, top: clamped });
  }, [open, position]);

  useEffect(() => {
    if (!open || !position || !focusOnOpen.current) return;
    focusOnOpen.current = false;
    panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open, position]);

  // Dismiss on a click elsewhere, Escape, or anything that would leave the
  // panel floating away from its button.
  useEffect(() => {
    if (!open) return;
    const isInside = (target: EventTarget | null) =>
      target instanceof Node &&
      (buttonRef.current?.contains(target) || panelRef.current?.contains(target));
    const onPointerDown = (e: MouseEvent) => {
      if (!isInside(e.target)) onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      onClose();
      buttonRef.current?.focus();
    };
    const onReflow = (e: Event) => {
      if (!isInside(e.target)) onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, onClose]);

  const active = isItemActive(pathname, item);
  const Icon = item.icon;
  const visibleChildren = (item.children ?? []).filter((c) => !c.hidden);
  if (visibleChildren.length === 0) return null;

  // Arrow keys move between entries, as a menu should; Tab leaves the menu
  // and carries on from the button, since the panel sits at the end of the
  // document and tabbing onward from it would skip the rest of the sidebar.
  const onPanelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab') {
      onClose();
      buttonRef.current?.focus();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const entries = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    const current = entries.indexOf(document.activeElement as HTMLElement);
    const step = e.key === 'ArrowDown' ? 1 : -1;
    entries[(current + step + entries.length) % entries.length]?.focus();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          // `detail` is 0 for a click synthesised from Enter / Space.
          focusOnOpen.current = !open && e.detail === 0;
          onToggle();
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={item.label}
        title={item.label}
        className={cn(
          'flex w-full items-center rounded-lg',
          NAV_ROW,
          active
            ? 'bg-blue-600 text-white'
            : open
              ? 'bg-slate-700 text-white'
              : 'text-slate-300 hover:bg-slate-700 hover:text-white',
        )}
      >
        <Icon className="h-5 w-5 shrink-0" />
      </button>
      {open &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="menu"
            aria-label={item.label}
            onKeyDown={onPanelKeyDown}
            style={{ top: position.top, left: position.left }}
            className="fixed z-[60] min-w-48 rounded-lg border border-slate-700 bg-slate-800 py-1.5 text-white shadow-xl"
          >
            <p className="px-3 pb-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">
              {item.label}
            </p>
            <div className="space-y-1 px-1.5">
              {visibleChildren.map((child) => (
                <Link
                  key={child.id}
                  to={child.href}
                  role="menuitem"
                  // Covers a click on the page already showing — the route
                  // doesn't change, so the pathname effect wouldn't close it.
                  onClick={onClose}
                  className={cn(
                    'block rounded-md px-3 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
                    isChildActive(pathname, child)
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white',
                  )}
                >
                  {child.label}
                </Link>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
